import { createBrowserRouter, Navigate, type RouteObject } from "react-router";
import { ROUTES } from "@/shared/constants/routes";
import { AppShell } from "@/shared/layout/AppShell";
import { RequireAdmin, RequireOperator } from "./guards";
import { NotFoundPage } from "./NotFoundPage";
import { RouteErrorPage } from "./RouteErrorPage";

/**
 * Lazy route table (rules/01 §1): importable without a router for tests. Pages are loaded on first
 * navigation; `handle.title` feeds the top bar. Two tiers: the public login page and the operator shell.
 */
export const routes: RouteObject[] = [
  {
    path: ROUTES.login,
    lazy: async () => ({ Component: (await import("@/modules/auth/pages/LoginPage")).LoginPage }),
    errorElement: <RouteErrorPage />,
  },
  {
    path: "/",
    element: (
      <RequireOperator>
        <AppShell />
      </RequireOperator>
    ),
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <Navigate to={ROUTES.tenants} replace /> },
      {
        path: "tenants",
        handle: { title: "Tenants" },
        lazy: async () => ({ Component: (await import("@/modules/tenants/pages/TenantsPage")).TenantsPage }),
      },
      {
        path: "tenants/:tenantId",
        handle: { title: "Tenant" },
        lazy: async () => ({
          Component: (await import("@/modules/tenants/pages/TenantDetailPage")).TenantDetailPage,
        }),
      },
      {
        // M9 §6: reached from a terminal row (tenant page or fleet), never from the sidebar.
        path: "tenants/:tenantId/devices/:deviceId/console",
        handle: { title: "Device console" },
        lazy: async () => ({
          Component: (await import("@/modules/console/pages/DeviceConsolePage")).DeviceConsolePage,
        }),
      },
      {
        // M11 (ADR-0015 §5): money across organisations. Read-only, so any operator may open it.
        path: "billing",
        handle: { title: "Billing" },
        lazy: async () => ({
          Component: (await import("@/modules/billing/pages/BillingOverviewPage")).BillingOverviewPage,
        }),
      },
      {
        // M11: the catalogue changes what every organisation is charged — platform admin only, the
        // same gate the API enforces with `requireOperatorAdmin`.
        path: "catalogue",
        handle: { title: "Catalogue" },
        lazy: async () => {
          const { CataloguePage } = await import("@/modules/catalogue/pages/CataloguePage");
          return {
            Component: () => (
              <RequireAdmin action="catalog.manage">
                <CataloguePage />
              </RequireAdmin>
            ),
          };
        },
      },
      {
        // M20 (ADR-0024): the platform's SMS price and limits. Any operator reads it; the form itself gates
        // saving to admins (`sms.manage`), the same split the API enforces — so no `RequireAdmin` here.
        path: "sms",
        handle: { title: "SMS" },
        lazy: async () => ({
          Component: (await import("@/modules/sms/pages/SmsSettingsPage")).SmsSettingsPage,
        }),
      },
      {
        path: "sms/templates",
        handle: { title: "SMS templates" },
        lazy: async () => ({
          Component: (await import("@/modules/sms/pages/SmsTemplatesPage")).SmsTemplatesPage,
        }),
      },
      {
        // Reached from the overview strip's counts, not from the sidebar.
        path: "sms/messages",
        handle: { title: "SMS messages" },
        lazy: async () => ({
          Component: (await import("@/modules/sms/pages/SmsMessagesPage")).SmsMessagesPage,
        }),
      },
      {
        // M26 (ADR-0031, contracts §22): the platform's statutory rule sets and tax years, and the readiness list.
        // Any operator reads them; every write is gated inside the page by `payroll_law.manage` (the API:
        // `requireOperatorAdmin`), so no `RequireAdmin` here — the SMS templates split.
        path: "payroll-law",
        handle: { title: "Payroll law" },
        lazy: async () => ({
          Component: (await import("@/modules/payrollLaw/pages/PayrollLawPage")).PayrollLawPage,
        }),
      },
      {
        path: "payroll-law/rule-sets/:id",
        handle: { title: "Rule set" },
        lazy: async () => ({
          Component: (await import("@/modules/payrollLaw/pages/PayrollRuleSetPage")).PayrollRuleSetPage,
        }),
      },
      {
        path: "payroll-law/tax-years/:id",
        handle: { title: "Tax year" },
        lazy: async () => ({
          Component: (await import("@/modules/payrollLaw/pages/PayrollTaxYearPage")).PayrollTaxYearPage,
        }),
      },
      {
        path: "fleet",
        handle: { title: "Fleet" },
        lazy: async () => ({ Component: (await import("@/modules/fleet/pages/FleetPage")).FleetPage }),
      },
      {
        path: "mobile-releases",
        handle: { title: "Mobile releases" },
        lazy: async () => ({
          Component: (await import("@/modules/mobileReleases/pages/MobileReleasesPage")).MobileReleasesPage,
        }),
      },
      {
        // M14 follow-up: any operator reads it; the page itself gates editing and verifying to admins
        // (`device_models.manage`), the same split the API enforces — so no `RequireAdmin` here.
        path: "device-models",
        handle: { title: "Device models" },
        lazy: async () => ({
          Component: (await import("@/modules/deviceModels/pages/DeviceModelsPage")).DeviceModelsPage,
        }),
      },
      {
        path: "unclaimed",
        handle: { title: "Unclaimed terminals" },
        lazy: async () => ({
          Component: (await import("@/modules/fleet/pages/UnclaimedPage")).UnclaimedPage,
        }),
      },
      {
        path: "queues",
        handle: { title: "Queues" },
        lazy: async () => ({ Component: (await import("@/modules/queues/pages/QueuesPage")).QueuesPage }),
      },
      {
        path: "audit",
        handle: { title: "Audit" },
        lazy: async () => ({ Component: (await import("@/modules/audit/pages/AuditPage")).AuditPage }),
      },
      {
        path: "operators",
        handle: { title: "Operators" },
        lazy: async () => {
          const { OperatorsPage } = await import("@/modules/operators/pages/OperatorsPage");
          return {
            Component: () => (
              <RequireAdmin action="operators.manage">
                <OperatorsPage />
              </RequireAdmin>
            ),
          };
        },
      },
      {
        path: "reviewers",
        handle: { title: "Reviewers" },
        lazy: async () => {
          const { ReviewersPage } = await import("@/modules/reviewers/pages/ReviewersPage");
          return {
            Component: () => (
              <RequireAdmin action="reviewers.manage">
                <ReviewersPage />
              </RequireAdmin>
            ),
          };
        },
      },
      { path: "*", handle: { title: "Not found" }, element: <NotFoundPage /> },
    ],
  },
];

export function createAppRouter() {
  return createBrowserRouter(routes);
}
